import React, { useRef, useState } from 'react';
import { Button, Icon } from '../../campaign-os';
import { parseAddresses, mergeCc, ccSheetAoA } from '../data/ccList';

/**
 * The editable copy list behind a campaign's CC and BCC fields.
 *
 * One component serves both, so the two are provably the same control rather than two
 * drifting copies of the same 150 lines: same spreadsheet round trip, same de-duplication,
 * same "fill from the selected audience". Only the wording and the addresses differ.
 *
 * List state is owned by the caller (it has to travel with the send); the draft box and
 * the show-all toggle are local, because nothing outside this panel cares about them.
 *
 * Nothing here scrolls: the dialog grows to its content and the page scrolls as one
 * surface, so a long list is truncated behind a toggle rather than given an inner
 * scrollbar that would trap the wheel.
 */

const RAIL_LABEL = 'mb-1 block text-[11px] font-semibold uppercase tracking-[0.08em] text-cos-ink-muted';
const RAIL_FIELD =
  'w-full rounded-lg border border-cos-line bg-white/80 px-2.5 py-2 text-[13px] text-cos-ink focus:border-cos-teal/50 focus:outline-none focus:ring-2 focus:ring-cos-teal/15 transition';

/** Rows shown before the "show all" toggle. */
const PREVIEW_ROWS = 12;

const num = (n) => Number(n || 0).toLocaleString('en-IN');

export function RecipientListEditor({
  label,
  entries,
  onChange,
  onFillFromAudience,
  onUploadFile,
  max,
  loading = false,
  capped = false,
  emptyHint,
  footnote,
  /** Addresses already spoken for by an earlier field — shown as a warning, not removed. */
  takenElsewhere = [],
  onError,
}) {
  const [draft, setDraft] = useState('');
  const [showAll, setShowAll] = useState(false);
  const fileRef = useRef(null);

  const addTyped = () => {
    const added = parseAddresses(draft).map((email) => ({ email, label: '', vendor_code: null }));
    if (added.length === 0) {
      onError?.('That is not an email address');
      return;
    }
    onChange(mergeCc(entries, added));
    setDraft('');
  };

  const remove = (email) =>
    onChange(entries.filter((e) => e.email.toLowerCase() !== email.toLowerCase()));

  const download = () => {
    // Imported lazily: xlsx is a large dependency and only this button needs it.
    import('xlsx').then((XLSX) => {
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(ccSheetAoA(entries)), label);
      XLSX.writeFile(wb, `${label.toLowerCase()}-list.xlsx`);
    });
  };

  const taken = new Set(takenElsewhere.map((e) => String(e).toLowerCase()));
  const overlap = entries.filter((e) => taken.has(e.email.toLowerCase()));
  const shown = showAll ? entries : entries.slice(0, PREVIEW_ROWS);

  return (
    <div className="rounded-xl border border-cos-line bg-white/60 px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className={`${RAIL_LABEL} mb-0`}>
          {label} · {num(entries.length)}
          {entries.length > max ? ` (max ${num(max)})` : ''}
        </p>
        <div className="flex flex-wrap gap-1">
          <Button variant="ghost" size="sm" icon="group_add" onClick={onFillFromAudience} disabled={loading}>
            {loading ? 'Reading…' : 'Fill from audience'}
          </Button>
          <Button variant="ghost" size="sm" icon="upload_file" onClick={() => fileRef.current?.click()} disabled={loading}>
            Upload
          </Button>
          <Button variant="ghost" size="sm" icon="download" onClick={download} disabled={entries.length === 0}>
            Excel
          </Button>
        </div>
      </div>
      <input
        ref={fileRef}
        type="file"
        accept=".xlsx,.xls,.csv"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (f) onUploadFile(f);
        }}
      />

      {capped ? (
        <p className="mt-1.5 text-[11px] leading-[16px] text-cos-status-amber">
          The filter matches more vendors than one email can copy — only the first {num(max)} were taken.
        </p>
      ) : null}

      {overlap.length > 0 ? (
        <p className="mt-1.5 text-[11px] leading-[16px] text-cos-status-amber">
          {num(overlap.length)} of these {overlap.length === 1 ? 'is' : 'are'} already in an earlier field and will be
          dropped from {label} — the provider refuses the whole send if one address appears twice.
        </p>
      ) : null}

      {entries.length === 0 ? (
        <p className="mt-2 text-[12px] leading-[17px] text-cos-ink-secondary">{emptyHint}</p>
      ) : (
        <ul className="mt-2 flex flex-col gap-1">
          {shown.map((c) => (
            <li
              key={c.email}
              className="flex items-center gap-2 rounded-lg border border-cos-line-subtle bg-white/70 px-2.5 py-1.5"
            >
              <span className="min-w-0 flex-1 truncate text-[12px] text-cos-ink">
                {c.label ? (
                  <>
                    {c.label} <span className="mono text-cos-ink-secondary">{c.email}</span>
                  </>
                ) : (
                  <span className="mono">{c.email}</span>
                )}
              </span>
              <button
                type="button"
                onClick={() => remove(c.email)}
                aria-label={`Remove ${c.email} from ${label}`}
                className="shrink-0 rounded p-0.5 text-cos-ink-muted transition-colors hover:text-cos-status-red-ink"
              >
                <Icon name="close" className="text-[15px]" />
              </button>
            </li>
          ))}
        </ul>
      )}
      {entries.length > PREVIEW_ROWS ? (
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          className="mt-1.5 text-[12px] font-semibold text-cos-teal-deep hover:underline"
        >
          {showAll ? 'Show fewer' : `Show all ${num(entries.length)} — every one is removable`}
        </button>
      ) : null}

      <div className="mt-2 flex gap-1.5">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return;
            e.preventDefault();
            addTyped();
          }}
          placeholder="Add an address, or paste several"
          className={RAIL_FIELD}
        />
        <Button variant="secondary" size="sm" icon="add" onClick={addTyped} disabled={!draft.trim()}>
          Add
        </Button>
      </div>

      {footnote ? (
        <p className="mt-2 text-[11px] leading-[16px] text-cos-ink-secondary">{footnote}</p>
      ) : null}
    </div>
  );
}

export default RecipientListEditor;
